import React from "react";
import {ArrowRight, CalendarDays, ClipboardCheck, PlayCircle, Target, TrendingUp} from "lucide-react";

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-white text-slate-950">
      {/* Background accents */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_18%,rgba(211,31,43,0.10),transparent_28%),linear-gradient(180deg,#ffffff_0%,#f7f8fa_68%,#111827_68%,#080b10_100%)]" />
      <div className="absolute right-0 top-16 h-64 w-1/2 opacity-10">
        <svg viewBox="0 0 620 260" className="h-full w-full" fill="none">
          <path d="M35 215L175 122L285 160L435 55L585 28" stroke="#D31F2B" strokeWidth="18" strokeLinecap="round" />
          <path d="M585 28L548 24L576 61L585 28Z" fill="#D31F2B" />
          <path d="M105 205V140M250 205V98M395 205V70M535 205V35" stroke="#D31F2B" strokeWidth="22" opacity=".5" />
        </svg>
      </div>

      <div className="relative mx-auto grid min-h-[760px] max-w-7xl grid-cols-1 items-center gap-10 px-6 py-12 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
        {/* Left content */}
        <div className="z-10 max-w-2xl pt-4 lg:pt-0">
          <h1 className="text-6xl font-black leading-[0.95] tracking-tight md:text-7xl lg:text-8xl">
            <span className="block text-slate-950">be the king</span>
            <span className="block text-[#D31F2B]">of things</span>
            <span className="block text-[#D31F2B]">that matter</span>
          </h1>

          <p className="mt-8 max-w-xl text-xl leading-8 text-slate-600 md:text-2xl">
            The all-in-one productivity system to help you focus, prioritize, and achieve what truly matters.
          </p>

          <div className="mt-9 flex flex-col gap-4 sm:flex-row">
            <a
              href="#get-started"
              className="inline-flex items-center justify-center rounded-xl bg-[#D31F2B] px-7 py-4 text-base font-bold text-white shadow-lg shadow-red-600/20 transition hover:-translate-y-0.5 hover:bg-[#B91824]"
            >
              Start Free Today
              <ArrowRight className="ml-3 h-5 w-5" />
            </a>
            <a
              href="#demo"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-7 py-4 text-base font-bold text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50"
            >
              <PlayCircle className="mr-3 h-5 w-5" />
              Watch Demo
            </a>
          </div>
        </div>

        {/* Product mockup */}
        <div className="relative z-10 hidden lg:block">
          <div className="relative ml-auto w-[680px] rotate-3 rounded-[2rem] border-[14px] border-slate-900 bg-white p-6 shadow-2xl shadow-slate-950/25">
            <div className="grid grid-cols-[150px_1fr] gap-6">
              <aside className="border-r border-slate-100 pr-4">
                <div className="mb-8 flex items-center gap-2">
                  <img src="/images/matterking-logo.png" alt="Matter King" className="w-28" />
                </div>
                {["Dashboard", "Tasks", "Projects", "Calendar", "Goals", "Notes", "Analytics", "Settings"].map((item, index) => (
                  <div
                    key={item}
                    className={`mb-2 rounded-lg px-3 py-2 text-sm font-medium ${
                      index === 0 ? "bg-red-50 text-[#D31F2B]" : "text-slate-500"
                    }`}
                  >
                    {item}
                  </div>
                ))}
              </aside>

              <main>
                <p className="text-sm font-semibold text-slate-500">Dashboard</p>
                <h2 className="mt-2 text-2xl font-black text-slate-950">Welcome back, Alex!</h2>
                <p className="mt-1 text-sm text-slate-500">Here's what's happening with your goals today.</p>

                <div className="mt-7 grid grid-cols-4 gap-4">
                  {[
                    ["Tasks Today", "12", "of 18 completed"],
                    ["Projects", "7", "active"],
                    ["Goals", "4", "in progress"],
                    ["Focus Time", "3.5h", "today"],
                  ].map(([label, value, note]) => (
                    <div key={label} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold text-slate-500">{label}</p>
                      <p className="mt-3 text-3xl font-black text-slate-950">{value}</p>
                      <p className="mt-1 text-xs text-slate-400">{note}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 grid grid-cols-2 gap-5">
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-black text-slate-900">Top Priorities</h3>
                    {["Launch marketing campaign", "Finish product roadmap", "Workout", "Read 30 minutes"].map((task, index) => (
                      <div key={task} className="mt-4 flex items-center justify-between text-sm">
                        <span className="text-slate-600">{task}</span>
                        <span className={`rounded-full px-2 py-1 text-xs font-bold ${index < 2 ? "bg-red-50 text-[#D31F2B]" : "bg-slate-100 text-slate-500"}`}>
                          {index < 2 ? "High" : index === 2 ? "Med" : "Low"}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-black text-slate-900">Goal Progress</h3>
                    <p className="mt-5 text-sm text-slate-500">Q2 Business Goals</p>
                    <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full w-[68%] rounded-full bg-[#D31F2B]" />
                    </div>
                    <p className="mt-3 text-sm font-bold text-slate-700">68% complete</p>
                  </div>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom feature strip */}
      <div className="relative z-10 bg-[#090C12] px-6 py-8 text-white lg:-mt-32 lg:pt-10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Target, "FOCUS", "Eliminate distractions and stay on track"],
            [ClipboardCheck, "PRIORITIZE", "Focus on what truly matters"],
            [CalendarDays, "PLAN", "Organize your day and your goals"],
            [TrendingUp, "ACHIEVE", "Make progress and get results"],
          ].map(([Icon, title, text]) => (
            <div key={title} className="flex items-center gap-4 border-white/10 lg:border-r lg:pr-8 last:border-r-0">
              <Icon className="h-12 w-12 shrink-0 text-[#D31F2B]" />
              <div>
                <h3 className="font-black tracking-wide">{title}</h3>
                <p className="mt-1 text-sm leading-6 text-white/70">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
